import { lazy, Suspense } from 'react';
import ErrorBoundary from 'components/ErrorBoundary';

/**
 * R6 vertical isolation on the product page: exactly ONE module's widgets
 * render -- the product's own `experience_module` (resolved server-side
 * from its category). Each widget is its own lazy chunk, loaded only on
 * product pages of that module.
 */
const DETAIL_WIDGETS = {
  fashion: lazy(() => import('./widgets/FashionWidget')),
  beauty: lazy(() => import('./widgets/BeautyWidget')),
  fragrance: lazy(() => import('./widgets/FragranceWidget')),
  food: lazy(() => import('./widgets/FoodWidget')),
  electronics_accessories: lazy(() => import('./widgets/ElectronicsWidget')),
  home: lazy(() => import('./widgets/HomeWidget')),
  grocery_origin: lazy(() => import('./widgets/GroceryWidget')),
};
const FoodOptions = lazy(() => import('./widgets/FoodOptions'));
const ActualSize = lazy(() => import('./widgets/ActualSize'));

// Modules whose products are not physical goods (no "Actual size").
const NON_PHYSICAL = new Set(['food']);

export function moduleOf(product) {
  return product?.experience_module || 'generic';
}

function hasAttributes(product) {
  return product?.attributes && Object.keys(product.attributes).length > 0;
}

function hasSize(product) {
  const d = product?.dimensions || {};
  return ['length', 'width', 'height'].filter((k) => Number(d[k]) > 0).length >= 2;
}

/** Display widgets for the product's own module (+ Actual size for physical goods). */
export function ModuleDetails({ product }) {
  const key = moduleOf(product);
  const Widget = DETAIL_WIDGETS[key];
  const showWidget = Widget && hasAttributes(product);
  const showSize = !NON_PHYSICAL.has(key) && hasSize(product);
  if (!showWidget && !showSize) return null;
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={null}>
        <div className="space-y-6" data-testid="module-details" data-module={key}>
          {showWidget && <Widget product={product} attributes={product.attributes} />}
          {showSize && <ActualSize dimensions={product.dimensions} />}
        </div>
      </Suspense>
    </ErrorBoundary>
  );
}

/** Buy-box options (food modifiers + "My usual"); nothing for other modules. */
export function ModuleBuyOptions({ product, modifiers, onModifiersChange, onReorder }) {
  if (moduleOf(product) !== 'food' || !hasAttributes(product)) return null;
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={null}>
        <FoodOptions product={product} modifiers={modifiers} onChange={onModifiersChange} onReorder={onReorder} />
      </Suspense>
    </ErrorBoundary>
  );
}

/** Food product switched off by the restaurant? */
export function isMenuUnavailable(product) {
  return moduleOf(product) === 'food' && product?.attributes?.menu_available === false;
}
